import { useCallback, useEffect, useState } from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PhotoIcon,
} from "@heroicons/react/24/outline";
import { FiThumbsUp } from "react-icons/fi";
import { getPostsByProfileId } from "../../../services/api";
import {
  FileMetadataDto,
  PostResponseDto,
} from "../../../types/types";
import { formatDateTime } from "../../../utils/formatUtils";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

type PostSectionProps = {
  profileId: string | number;
};

type PostsPage = {
  content?: PostResponseDto[];
  posts?: PostResponseDto[];
  totalPages?: number;
  totalElements?: number;
  number?: number;
};

export const PostSection = ({ profileId }: PostSectionProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("publicProfile");
  const pageSize = 10;
  const [posts, setPosts] = useState<PostResponseDto[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPosts = useCallback(async (page = 0) => {
    if (!profileId) return;
    try {
      setIsLoading(true);
      setError(null);
      const res: PostsPage | PostResponseDto[] = await getPostsByProfileId(profileId, page, pageSize);
      const content = Array.isArray(res) ? res : res.content ?? res.posts ?? [];
      const nextAvailable = Array.isArray(res)
        ? content.length === pageSize
        : page + 1 < (res.totalPages ?? 1);
      setPosts(content);
      setCurrentPage(Array.isArray(res) ? page : res.number ?? page);
      setTotalPages(Array.isArray(res) ? 1 : res.totalPages ?? 1);
      setTotalElements(Array.isArray(res) ? content.length : res.totalElements ?? content.length);
      setHasNextPage(nextAvailable);
    } catch (err: any) {
      setError(typeof err === "string" ? err : t("failed_to_fetch_posts"));
    } finally {
      setIsLoading(false);
    }
  }, [pageSize, profileId, t]);

  useEffect(() => {
    setPosts([]);
    setCurrentPage(0);
    setTotalPages(1);
    setTotalElements(0);
    setHasNextPage(false);
    loadPosts(0);
  }, [loadPosts, profileId]);

  const handlePageChange = (page: number) => {
    if (page < 0 || page === currentPage || isLoading || (page > currentPage && !hasNextPage)) return;
    loadPosts(page);
  };

  const getPostFile = (post: PostResponseDto): FileMetadataDto | null => {
    const flexiblePost = post as PostResponseDto & {
      fileMetadata?: FileMetadataDto | null;
      fileMetadataDto?: FileMetadataDto | null;
      fileMetadataList?: FileMetadataDto[];
    };
    return (
      flexiblePost.file ??
      flexiblePost.fileMetadata ??
      flexiblePost.fileMetadataDto ??
      flexiblePost.fileMetadataList?.[0] ??
      null
    );
  };

  const renderMedia = (post: PostResponseDto) => {
    const file = getPostFile(post);
    if (!file?.fileUrl) return null;

    const isVideo =
      file.fileType?.startsWith("video") ||
      file.fileName?.match(/\.(mp4|webm|ogg|mov)$/i);

    return (
      <div className="overflow-hidden border-y border-gray-100 bg-gray-100">
        {isVideo ? (
          <div className="flex w-full items-center justify-center bg-black">
            <video
              src={file.fileUrl}
              title={file.fileName || post.title}
              className="w-full max-h-[280px] object-contain"
              controls
            />
          </div>
        ) : (
          <div className="w-full overflow-hidden bg-gray-100">
            <img
              src={file.fileUrl}
              alt={file.fileName || post.title}
              className="block w-full h-auto object-contain"
              onError={(e) => {
                e.currentTarget.src = "https://via.placeholder.com/300";
              }}
            />
          </div>
        )}
      </div>
    );
  };

  const getInitials = (firstName?: string, lastName?: string) =>
    `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase() || "P";

  const navigateToProfile = (targetProfileId?: string | number | null) => {
    if (!targetProfileId) return;
    const profilePath = location.pathname === "/view-profile" ? "/view-profile" : "/profile";
    navigate(`${profilePath}?profileId=${targetProfileId}`);
  };

  return (
    <div className="mt-4">
      <div className="mb-3 flex max-w-lg items-center justify-between">
        <h2 className="text-base font-semibold text-gray-800">{t("posts")}</h2>
        <span className="text-xs text-gray-400">
          {totalElements} {t(totalElements === 1 ? "post" : "posts_count")}
        </span>
      </div>

      {isLoading && posts.length === 0 ? (
        <div className="flex w-full items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-t-2 border-gray-500" />
        </div>
      ) : error ? (
        <div className="mx-auto max-w-lg rounded-lg border border-red-100 bg-red-50 p-5 text-sm text-red-600">
          {error}
        </div>
      ) : posts.length === 0 ? (
        <div className="mx-auto flex max-w-lg flex-col items-center justify-center rounded-lg border border-gray-100 bg-white py-8 text-center">
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
            <PhotoIcon className="h-5 w-5 text-gray-300" />
          </div>
          <p className="text-sm text-gray-400">{t("no_posts_yet")}</p>
        </div>
      ) : (
        <div className="mx-auto max-h-[520px] max-w-lg space-y-3 overflow-y-auto pr-2">
          {posts.map((post) => {
            const profileName = `${post.profile?.firstName ?? ""} ${post.profile?.lastName ?? ""}`.trim();
            const postProfileId = post.profile?.id;

            return (
              <article
                key={post.id}
                className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
              >
                <div className="px-3 pb-2 pt-2.5">
                  <div className="flex items-start gap-2">
                    <button
                      type="button"
                      onClick={() => navigateToProfile(postProfileId)}
                      className="relative shrink-0 rounded-full focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                      aria-label={t("view_profile_aria", {
                        name: profileName || t("profile"),
                      })}
                    >
                      {post.profile?.profileImage ? (
                        <img
                          src={post.profile.profileImage}
                          alt={profileName || t("profile")}
                          className="h-9 w-9 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-xs font-semibold text-white shadow-sm">
                          {getInitials(post.profile?.firstName, post.profile?.lastName)}
                        </div>
                      )}
                      {post.profile?.verified && (
                        <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white">
                          <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
                        </span>
                      )}
                    </button>

                    <div className="min-w-0 flex-1 text-left">
                      <button
                        type="button"
                        onClick={() => navigateToProfile(postProfileId)}
                        className="block max-w-full truncate text-left text-xs font-semibold text-gray-900 hover:text-blue-600 focus:outline-none focus:underline"
                      >
                        {profileName || t("profile")}
                      </button>
                      {post.createdAt && (
                        <p className="mt-0.5 text-xs text-gray-400">
                          {formatDateTime(post.createdAt)}
                        </p>
                      )}
                    </div>
                  </div>

                  {(post.title || post.content) && (
                    <div className="mt-2.5 text-left">
                      {post.title && (
                        <h3 className="break-words text-xs font-semibold leading-snug text-gray-900">
                          {post.title}
                        </h3>
                      )}
                      {post.content && (
                        <p className="mt-1 whitespace-pre-wrap break-words text-xs leading-relaxed text-gray-700">
                          {post.content}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {renderMedia(post)}

                <div className="flex items-center border-t border-gray-100 px-3 py-1.5 text-[11px] text-gray-500">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-white">
                      <FiThumbsUp className="h-2.5 w-2.5" />
                    </span>
                    {post.totalLikes ?? 0} {t((post.totalLikes ?? 0) === 1 ? "like" : "likes")}
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {(currentPage > 0 || hasNextPage || totalPages > 1) && (
        <div className="mx-auto mt-3 flex max-w-lg flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-3">
          <p className="text-xs text-gray-500">
            {t("page")} {currentPage + 1}{totalPages > 1 ? ` ${t("of")} ${totalPages}` : ""}
          </p>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0 || isLoading}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label={t("previous_posts_page")}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              disabled
              className="h-8 min-w-8 rounded-md border border-blue-600 bg-blue-600 px-2 text-xs font-medium text-white shadow-sm"
            >
              {currentPage + 1}
            </button>
            <button
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!hasNextPage || isLoading}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label={t("next_posts_page")}
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
